import { useState, useEffect, useCallback } from 'react';
import {
  shiftMasterApi,
  SEARCH_FIELD_OPTIONS,
  STATUS_OPTIONS,
} from '../api/shiftMasterApi';
import { appToast } from '../../../../components/AppToast';
import { appConfirm } from '../../../../components/AppConfirmModal';
import { useExportJob } from '../../../../hooks/useExportJob';
import { executePrintReport } from '../../../../utils/printReportUtil';

const REPORT_COLUMNS = [
  { key: 'name', label: 'Shift Name' },
  { key: 'alias', label: 'Shift Code' },
  { key: 'inTime', label: 'Start Time' },
  { key: 'outTime', label: 'End Time' },
  { key: 'shiftType', label: 'Shift Type' },
  { key: 'status', label: 'Status' },
];

export function useShiftMasterList() {
  const [searchField, setSearchField] = useState('Name');
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('Active');

  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const loadRecords = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await shiftMasterApi.getRecords({
        searchField,
        searchText,
        status: statusFilter,
      });
      setRecords(data);
    } catch (err) {
      appToast.error('Failed to load Shift Master records.');
    } finally {
      setIsLoading(false);
    }
  }, [searchField, searchText, statusFilter]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const handleSearch = () => {
    setPage(1);
    loadRecords();
  };

  const resetFilters = () => {
    setSearchField('Name');
    setSearchText('');
    setStatusFilter('Active');
    setPage(1);
  };

  const handleStatusTabChange = (key) => {
    setStatusFilter(key);
    setPage(1);
  };

  const handleArchive = async (id, name, currentStatus) => {
    const isInactive = currentStatus === 'Inactive';
    const actionLabel = isInactive ? 'unarchive' : 'archive';
    const confirmed = await appConfirm({
      title: `${isInactive ? 'Unarchive' : 'Archive'} Shift`,
      message: `Are you sure you want to ${actionLabel} shift "${name || id}"?`,
      confirmText: isInactive ? 'Unarchive Shift' : 'Archive Shift',
      variant: isInactive ? 'primary' : 'warning',
      icon: isInactive ? 'unarchive' : 'archive',
    });
    if (!confirmed) {
      return;
    }
    try {
      await shiftMasterApi.archiveRecord(id);
      appToast.success(`Shift "${name || id}" ${isInactive ? 'unarchived' : 'archived'} successfully.`);
      loadRecords();
    } catch (err) {
      const msg = err.response?.data?.detail;
      appToast.error(typeof msg === 'string' ? msg : `Failed to ${actionLabel} shift.`);
    }
  };

  const handleDelete = async (id, name) => {
    const confirmed = await appConfirm({
      title: 'Delete Shift',
      message: `Are you sure you want to delete shift "${name || id}"? This action cannot be undone.`,
      confirmText: 'Delete Shift',
      variant: 'danger',
      icon: 'delete',
    });
    if (!confirmed) {
      return;
    }
    try {
      await shiftMasterApi.deleteRecord(id);
      appToast.success(`Shift "${name || id}" deleted successfully.`);
      loadRecords();
    } catch (err) {
      const msg = err.response?.data?.detail;
      appToast.error(typeof msg === 'string' ? msg : 'Failed to delete shift.');
    }
  };

  const { triggerExport, isExporting } = useExportJob({
    module: 'shift-master',
    jobType: 'EXPORT',
  });

  const tools = [
    { key: 'print', label: 'Print Report', icon: 'print' },
    { key: 'export_excel', label: 'Export Excel', icon: 'description' },
    { key: 'export_pdf', label: 'Export PDF', icon: 'picture_as_pdf' },
  ];

  const handleToolSelect = (toolKey) => {
    const exportFilters = {
      search: searchText,
      searchField,
      status: statusFilter,
    };

    if (toolKey === 'print') {
      executePrintReport({
        title: 'Shift Master Report',
        subtitle: `Status: ${statusFilter} | Total Records: ${records.length}`,
        columns: REPORT_COLUMNS,
        data: records,
        onExportPdf: () => triggerExport('pdf', exportFilters),
        onExportExcel: () => triggerExport('xlsx', exportFilters),
      });
    } else if (toolKey === 'export_excel') {
      triggerExport('xlsx', exportFilters);
    } else if (toolKey === 'export_pdf') {
      triggerExport('pdf', exportFilters);
    }
  };

  const paginatedRecords = records.slice((page - 1) * pageSize, page * pageSize);
  const totalPages = Math.ceil(records.length / pageSize) || 1;

  return {
    state: {
      records: paginatedRecords,
      rawRecords: records,
      isLoading,
      isExporting,
      tools,
      searchField,
      searchText,
      statusFilter,
      page,
      pageSize,
      totalCount: records.length,
      totalPages,
      searchFieldOptions: SEARCH_FIELD_OPTIONS,
      statusOptions: STATUS_OPTIONS,
    },
    actions: {
      setSearchField,
      setSearchText,
      setStatusFilter,
      handleStatusTabChange,
      setPage,
      handleSearch,
      resetFilters,
      handleArchive,
      handleDelete,
      handleToolSelect,
      triggerExport,
      refresh: loadRecords,
    },
  };
}
